/**
 * useDragReorder Hook
 *
 * Provides drag-to-reorder functionality for layers with visual feedback.
 * Supports both mouse and touch interactions.
 */

import { useState, useCallback, useRef, useEffect } from 'react';

export interface DragReorderState {
  isDragging: boolean;
  draggedItemId: string | null;
  dragOverItemId: string | null;
}

export interface UseDragReorderOptions {
  onReorder: (reorderedIds: string[]) => void;
  itemIds: string[];
}

export interface UseDragReorderReturn {
  dragState: DragReorderState;
  handleDragStart: (itemId: string) => void;
  handleDragEnd: () => void;
  handleDragOver: (itemId: string) => void;
  handleTouchMove: (e: React.TouchEvent) => void;
  getDragStyles: (itemId: string) => React.CSSProperties;
}

/**
 * Hook for drag-to-reorder functionality
 */
export function useDragReorder({
  onReorder,
  itemIds
}: UseDragReorderOptions): UseDragReorderReturn {
  const [dragState, setDragState] = useState<DragReorderState>({
    isDragging: false,
    draggedItemId: null,
    dragOverItemId: null
  });

  const dragTimerRef = useRef<NodeJS.Timeout | null>(null);
  const itemIdsRef = useRef<string[]>(itemIds);
  const isDraggingRef = useRef(false);
  const draggedItemIdRef = useRef<string | null>(null);

  // Update ref when itemIds changes
  useEffect(() => {
    itemIdsRef.current = itemIds;
  }, [itemIds]);

  // Sync refs with state for use in event handlers
  useEffect(() => {
    isDraggingRef.current = dragState.isDragging;
    draggedItemIdRef.current = dragState.draggedItemId;
  }, [dragState.isDragging, dragState.draggedItemId]);

  /**
   * Handle drag start (mouse or touch)
   */
  const handleDragStart = useCallback((itemId: string) => {
    // Delay to distinguish between click/tap and drag
    dragTimerRef.current = setTimeout(() => {
      setDragState({
        isDragging: true,
        draggedItemId: itemId,
        dragOverItemId: null
      });
    }, 150);
  }, []);

  /**
   * Handle drag end (mouse or touch)
   */
  const handleDragEnd = useCallback(() => {
    if (dragTimerRef.current) {
      clearTimeout(dragTimerRef.current);
      dragTimerRef.current = null;
    }

    if (dragState.draggedItemId && dragState.dragOverItemId) {
      const { draggedItemId, dragOverItemId } = dragState;

      // Reorder items
      const currentIds = [...itemIdsRef.current];
      const draggedIndex = currentIds.indexOf(draggedItemId);
      const targetIndex = currentIds.indexOf(dragOverItemId);

      if (draggedIndex !== -1 && targetIndex !== -1 && draggedIndex !== targetIndex) {
        // Remove dragged item
        currentIds.splice(draggedIndex, 1);
        // Insert at new position
        currentIds.splice(targetIndex, 0, draggedItemId);

        // Call reorder callback
        onReorder(currentIds);
      }
    }

    setDragState({
      isDragging: false,
      draggedItemId: null,
      dragOverItemId: null
    });
  }, [dragState, onReorder]);

  /**
   * Handle drag over (mouse hover)
   */
  const handleDragOver = useCallback((itemId: string) => {
    if (!dragState.isDragging || dragState.draggedItemId === itemId) {
      return;
    }

    setDragState(prev => ({
      ...prev,
      dragOverItemId: itemId
    }));
  }, [dragState.isDragging, dragState.draggedItemId]);

  /**
   * Handle touch move - find element under finger and update drag over state
   */
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    // Use refs for immediate access (state may be stale in event handler)
    if (!isDraggingRef.current) {
      return;
    }

    const touch = e.touches[0];
    if (!touch) return;

    // Find the element under the touch point
    const elementUnderTouch = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!elementUnderTouch) return;

    // Find the layer card container (look for data-layer-id attribute)
    const layerCard = elementUnderTouch.closest('[data-layer-id]');
    if (!layerCard) return;

    const layerId = layerCard.getAttribute('data-layer-id');
    if (!layerId || layerId === draggedItemIdRef.current) return;

    // Update drag over state
    setDragState(prev => ({
      ...prev,
      dragOverItemId: layerId
    }));
  }, []);

  /**
   * Get drag styles for an item
   */
  const getDragStyles = useCallback((itemId: string): React.CSSProperties => {
    const { isDragging, draggedItemId, dragOverItemId } = dragState;

    if (!isDragging) {
      return {};
    }

    if (itemId === draggedItemId) {
      return {
        opacity: 0.5,
        transform: 'scale(1.02)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        cursor: 'grabbing',
        zIndex: 1000,
        transition: 'none'
      };
    }

    if (itemId === dragOverItemId) {
      return {
        borderTop: '3px solid var(--em3-color-primary, #007bff)',
        marginTop: '8px',
        transition: 'margin 0.15s ease'
      };
    }

    return {};
  }, [dragState]);

  return {
    dragState,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleTouchMove,
    getDragStyles
  };
}
